import React from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Autocomplete,
  Paper,
  Chip,
} from '@mui/material';
import { Search } from '@mui/icons-material';

export interface FilterOption {
  value: any;
  label: string;
}

export interface FilterConfig {
  name: string;
  label: string;
  options: FilterOption[];
  multiple?: boolean;
  value?: any;
  onChange: (value: any) => void;
}

interface FilterBarProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  filters?: FilterConfig[];
}

/**
 * Reusable filter bar component with search and multiple filter dropdowns
 */
export const FilterBar: React.FC<FilterBarProps> = ({
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search...',
  filters = [],
}) => {
  return (
    <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'background.default', border: 1, borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Filter Dropdowns */}
        {filters.map((filter) => (
          <Autocomplete
            key={filter.name}
            size="small"
            options={filter.options}
            getOptionLabel={(option) => option.label}
            getOptionKey={(option) => `${filter.name}-${option.value}`}
            value={
              filter.multiple
                ? filter.options.filter((opt) => filter.value?.includes(opt.value))
                : filter.options.find((opt) => opt.value === filter.value) || null
            }
            onChange={(_event, newValue) => {
              if (filter.multiple) {
                filter.onChange(
                  Array.isArray(newValue) ? newValue.map((v) => v.value) : []
                );
              } else {
                filter.onChange(
                  newValue && !Array.isArray(newValue) ? newValue.value : null
                );
              }
            }}
            multiple={filter.multiple}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            renderInput={(params) => (
              <TextField
                {...params}
                label={filter.label}
                placeholder={filter.label}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={`${filter.name}-tag-${option.value}-${index}`}
                  label={option.label}
                  size="small"
                />
              ))
            }
            sx={{ minWidth: 200 }}
            clearOnEscape
            disableCloseOnSelect={filter.multiple}
          />
        ))}

        {/* Search Field */}
        <TextField
          placeholder={searchPlaceholder}
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          size="small"
          sx={{ minWidth: 250, flex: 1 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>
    </Paper>
  );
};
